import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { getLatestKycProcess } from 'apis/legitimizer/kyc/api';
import { isKYCProcessPending } from 'apis/legitimizer/kyc/utils';
import { useSessionStorage } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import MagnifyingGlassesPeople from 'common/illustrations/MagnifyingGlassesPeople';
import { Typography as T } from 'common/typography';

const Root = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  padding: 32px 24px 0;
`;

const Reminder = styled.div`
  background-color: ${colors.wolt8};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px;
  border-radius: 16px;
  gap: 24px;
`;

const StyledIllustration = styled(MagnifyingGlassesPeople)`
  width: 267px;
  height: 178px;
  min-width: 150px;
  display: none;

  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const Title = styled(T.Heading4)`
  margin-block-end: 16px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-block-end: 24px;
`;

const StyledButton = styled(Button)`
  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    width: auto;
  }
`;

const IdentityVerificationReminder = () => {
  const history = useHistory();

  const [, setSkipIDVerification] = useSessionStorage('skipIDVerification', '');

  const { data: latestProcess = null } = useQuery(['kyc/processes/latest'], () => getLatestKycProcess());
  const identityVerificationDone = !isKYCProcessPending(latestProcess);

  if (identityVerificationDone) {
    return null;
  }

  const handleCTAClick = () => {
    setSkipIDVerification('');

    history.push('/identity-verification/intro');
  };

  return (
    <Root>
      <Reminder>
        <div>
          <Title>
            <FormattedMessage id="compliance.kyc.reminder.title" />
          </Title>

          <Description>
            <T.Body2>
              <FormattedMessage id="compliance.kyc.reminder.content" />
            </T.Body2>

            <T.Body2>
              <FormattedMessage
                id="compliance.kyc.reminder.help"
                values={{
                  link: (
                    <a href="https://explore.wolt.com/merchant-kyc-faq" target="_blank" rel="noopener noreferrer">
                      <FormattedMessage id="compliance.kyc.reminder.faq" />
                    </a>
                  ),
                }}
              />
            </T.Body2>
          </Description>

          <StyledButton size="large" variant="blue" stretch onClick={handleCTAClick}>
            <FormattedMessage id="compliance.kyc.reminder.cta" />
          </StyledButton>
        </div>

        <StyledIllustration />
      </Reminder>
    </Root>
  );
};

export default IdentityVerificationReminder;
